/**
 * ISO 4217 minor units; this table is authoritative for split amounts.
 * Source: https://www.six-group.com/en/products-services/financial-information/market-reference-data/data-standards.html
 * Intl/CLDR is deliberately not used: WebKit and V8 disagree on TWD/HUF/IDR,
 * whose ISO exponent is 2. Formatting must explicitly use these exponents.
 * Append-only: add codes, never remove a code or change its exponent. Existing
 * rows would otherwise acquire a different monetary value when read again.
 */
export const CURRENCY_EXPONENTS: Readonly<Record<string, 0 | 2 | 3>> = Object.freeze({
  SGD: 2, USD: 2, EUR: 2, GBP: 2, AUD: 2, NZD: 2, CAD: 2, CHF: 2,
  CNY: 2, HKD: 2, TWD: 2, JPY: 0, KRW: 0, MYR: 2, THB: 2, IDR: 2,
  PHP: 2, VND: 0, INR: 2, AED: 2, SAR: 2, BHD: 3, KWD: 3, OMR: 3,
  JOD: 3, TND: 3, ISK: 0, HUF: 2, CZK: 2, PLN: 2, SEK: 2, NOK: 2,
  DKK: 2, ZAR: 2, BRL: 2, MXN: 2, TRY: 2,
  // Current and historical tender codes offered by runtime currency registries.
  AFN: 2, ALL: 2, AMD: 2, ANG: 2, AOA: 2, ARS: 2, AWG: 2, AZN: 2,
  BAM: 2, BBD: 2, BDT: 2, BGN: 2, BIF: 0, BMD: 2, BND: 2, BOB: 2,
  BSD: 2, BTN: 2, BWP: 2, BYN: 2, BZD: 2, CDF: 2, CLP: 0, COP: 2,
  CRC: 2, CUC: 2, CUP: 2, CVE: 2, DJF: 0, DOP: 2, DZD: 2, EGP: 2,
  ERN: 2, ETB: 2, FJD: 2, FKP: 2, GEL: 2, GHS: 2, GIP: 2, GMD: 2,
  GNF: 0, GTQ: 2, GYD: 2, HNL: 2, HRK: 2, HTG: 2, ILS: 2, IQD: 3,
  IRR: 2, JMD: 2, KES: 2, KGS: 2, KHR: 2, KMF: 0, KPW: 2, KYD: 2,
  KZT: 2, LAK: 2, LBP: 2, LKR: 2, LRD: 2, LSL: 2, LYD: 3, MAD: 2,
  MDL: 2, MGA: 2, MKD: 2, MMK: 2, MNT: 2, MOP: 2, MRU: 2, MUR: 2,
  MVR: 2, MWK: 2, MZN: 2, NAD: 2, NGN: 2, NIO: 2, NPR: 2, PAB: 2,
  PEN: 2, PGK: 2, PKR: 2, PYG: 0, QAR: 2, RON: 2, RSD: 2, RUB: 2,
  RWF: 0, SBD: 2, SCR: 2, SDG: 2, SHP: 2, SLE: 2, SLL: 2, SOS: 2,
  SRD: 2, SSP: 2, STN: 2, SVC: 2, SYP: 2, SZL: 2, TJS: 2, TMT: 2,
  TOP: 2, TTD: 2, TZS: 2, UAH: 2, UGX: 0, UYU: 2, UZS: 2, VES: 2,
  VUV: 0, WST: 2, XAF: 0, XCD: 2, XCG: 2, XOF: 0, XPF: 0, YER: 2,
  ZMW: 2, ZWG: 2, ZWL: 2,
});

/** Inclusive safe-integer input budget; admits everyday IDR/VND/KRW amounts. */
export const MAX_MINOR_INPUT = 1_000_000_000_000;

export function isCurrencyCode(value: string): boolean {
  return Object.prototype.hasOwnProperty.call(CURRENCY_EXPONENTS, value);
}

/** Unknown codes fail loudly instead of assuming two decimals (INV-7). */
export function exponentOf(currency: string): 0 | 2 | 3 {
  const exponent = CURRENCY_EXPONENTS[currency];
  if (!isCurrencyCode(currency) || exponent === undefined) {
    throw new Error(`Unsupported currency code: ${currency}`);
  }
  return exponent;
}
